/**
 * Slash commands filtered for email context.
 * Only includes commands that produce HTML compatible with email clients:
 * lists, blockquote, media, Loom, table, divider.
 * Excludes: headings, code blocks, checklists, details, youtube, embeds.
 */

import type { Instance, Props } from "tippy.js"
import { ReactRenderer } from "@tiptap/react"
import tippy from "tippy.js"

import type {
	OnKeyDownHandler,
	OnKeyDownHandlerSetter,
	SlashCommandDefinition,
	SlashCommandProps,
	SlashCommandSuggestionImpl,
} from "./types"
import type { SuggestionProps } from "@tiptap/suggestion"
import { suggestionPopupTarget } from "../suggestion-popup-target"
import {
	bulletedListCommand,
	dividerCommand,
	loomVideoCommand,
	mediaCommand,
	numberedListCommand,
	quoteCommand,
	tableAddColumnCommand,
	tableAddRowCommand,
	tableCommand,
	tableRemoveColumnCommand,
	tableRemoveRowCommand,
} from "./commands"
import SlashCommandsList from "./slash-commands-list"

const emailSlashCommandItems: SlashCommandDefinition[] = [
	numberedListCommand,
	bulletedListCommand,
	mediaCommand,
	loomVideoCommand,
	quoteCommand,
	dividerCommand,
	tableCommand,
	tableAddColumnCommand,
	tableRemoveColumnCommand,
	tableAddRowCommand,
	tableRemoveRowCommand,
]

export const emailSuggestion: SlashCommandSuggestionImpl = {
	items: ({ query }) => {
		const lowerQuery = query.toLocaleLowerCase()
		return emailSlashCommandItems
			.filter((item) => {
				const titleMatch = item.title.toLocaleLowerCase().includes(lowerQuery)
				const aliasMatch = item.aliases?.some((alias) =>
					alias.toLocaleLowerCase().includes(lowerQuery),
				)
				return titleMatch || aliasMatch
			})
			.slice(0, 15)
	},

	render: () => {
		let component: ReactRenderer<
			unknown,
			SuggestionProps<SlashCommandDefinition, SlashCommandProps>
		> | null = null
		let popup: Instance<Props>[] | null = null
		let onKeyDownHandler: OnKeyDownHandler = () => true
		const setOnKeyDownHandler: OnKeyDownHandlerSetter = (handler) => {
			onKeyDownHandler = handler
		}

		return {
			onStart: (props) => {
				component = new ReactRenderer(SlashCommandsList, {
					props: {
						...props,
						setOnKeyDownHandler,
					},
					editor: props.editor,
				})

				if (!props.clientRect) {
					return
				}

				const target = suggestionPopupTarget(props.editor)

				// @ts-expect-error tippy types are wrong
				popup = tippy("body", {
					getReferenceClientRect: props.clientRect,
					appendTo: () => target,
					content: component.element,
					showOnCreate: true,
					interactive: true,
					trigger: "manual",
					placement: "top-start",
					popperOptions: {
						strategy: "fixed",
						modifiers: [
							{
								name: "flip",
								options: { fallbackPlacements: ["bottom-start", "top-start"] },
							},
							{
								name: "preventOverflow",
								options: {
									padding: 8,
									altAxis: true,
									boundary:
										target === document.body ? "clippingParents" : target,
								},
							},
						],
					},
				})

				const resizeObserver = new ResizeObserver(() => {
					if (!popup?.[0]) return

					// eslint-disable-next-line @typescript-eslint/no-floating-promises
					popup[0].popperInstance?.update()
				})

				resizeObserver.observe(component.element)

				const containers = document.querySelectorAll(".scroll-container")
				containers.forEach((el) => {
					;(el as HTMLElement).style.overflow = "hidden"
				})
			},

			onUpdate(props) {
				if (!component) return
				component.updateProps(props)

				const { clientRect } = props
				if (!clientRect) {
					return
				}

				popup?.[0]?.setProps({
					getReferenceClientRect: () => {
						const c = clientRect()
						if (!c) {
							throw new Error("clientRect is null")
						}
						return c
					},
				})
			},

			onKeyDown(props) {
				if (props.event.key === "Escape") {
					popup?.[0]?.hide()
					return true
				}

				return onKeyDownHandler(props)
			},

			onExit() {
				popup?.[0]?.destroy()
				component?.destroy()
				document.body.classList.remove("slash-commands-open")
				document.documentElement.classList.remove("slash-commands-open")

				const containers = document.querySelectorAll(".scroll-container")
				containers.forEach((el) => {
					;(el as HTMLElement).style.overflow = ""
				})
			},
		}
	},
}
